import { EditorActions, useEditor } from '@candulabs/craft';
import { isString } from 'formik';
import flatten from 'lodash/flatten';

import { useMultiNodes } from 'craft/hooks/useMultiNodes';
import { useSelection } from 'craft/hooks/useSelection';
import { CraftNode } from 'craft/types';

/**
 * UseTypographyMarks is a hook to help us use marks in the typography objects in selection.
 *
 * It works by getting all the typographyNodeIds in selection,
 * filtering the `text` children, then using useMultiNodes to find the common marks.
 * toggling is then implementented to be able to toggle a mark on a per node basis.
 */
export const useTypographyNodeMarks = () => {
  const { selection } = useSelection();
  const { query, actions } = useEditor();

  /** Finds all the typography nodes in selection */
  const typographyNodeIds = selection.all.filter((nodeId: string) =>
    ['Typography', 'ListItem'].includes(query.node(nodeId)?.node?.data?.name),
  ) as string[];

  /** Get all the children the typography nodes. */
  const textNodeIds = flatten(
    typographyNodeIds.map((nodeId: string) => query.node(nodeId).descendants(true)),
  ).filter((nodeId: string) => {
    const { data } = query.node(nodeId).node || {};
    return (
      // make sure we don't get any void text node or links
      data?.name === 'Text' &&
      isString(data?.props?.childrenString) &&
      data?.props?.childrenString.length > 0
    );
  });

  // we convert from an array to a dictionary to make sure that the useMultiNodes
  // can diff correctly
  const { marksDic } = useMultiNodes(textNodeIds, (node: CraftNode) => ({
    marksDic: (node?.data?.custom?.custom?.marks || []).reduce(
      (acc: any, mark: string) => ({ ...acc, [mark]: true }),
      {},
    ),
  }));

  // a mark is common only if it's marked as true, meaning that every node has it.
  const marks = Object.entries(marksDic || {})
    .filter((entry) => entry[1] === true)
    .map(([key]) => key);

  const toggleMark = (newMark: string) => {
    actions.produceState((state, noHistoryActions: EditorActions) => {
      textNodeIds.forEach((textNodeId) => {
        let newMarks = state.nodes[textNodeId]?.data?.custom?.custom?.marks || [];
        // NOTE! we use marks for this checks, not newMarks. that's because
        // we want to set all of them or remove all of them across various nodes.
        if (marks.indexOf(newMark) > -1) {
          newMarks = newMarks.filter((m: string) => newMark !== m);
        } else {
          newMarks = [...newMarks, newMark];
        }
        noHistoryActions.setCustom(textNodeId, (node) => {
          // eslint-disable-next-line no-param-reassign
          node.custom = {
            ...node.custom,
            marks: newMarks,
          };
        });
      });
    });
  };
  return {
    marks: marks || [],
    toggleMark,
  };
};
